import { ReactNode, createContext, useContext, useMemo } from 'react';

/**
 * Router-capable path prefixes for modules that can be navigated to
 * via React Router without a full page reload.
 *
 * This is the single source of truth — update here when new module routes are added.
 */
const MODULE_ROUTER_PREFIXES: readonly string[] = [
  '/modules/gym',
  '/modules/vif',
  '/modules/wdyk',
  '/modules/wdyk2',
  '/modules/word-problems',
  '/modules/games/fraction-wheels',
  '/modules/games/k2-undo',
];

interface NavigationContextValue {
  routerPrefixes: readonly string[];
}

const NavigationContext = createContext<NavigationContextValue>({
  routerPrefixes: MODULE_ROUTER_PREFIXES,
});

// eslint-disable-next-line react-refresh/only-export-components
export function useNavigationContext(): NavigationContextValue {
  return useContext(NavigationContext);
}

/**
 * Overrides navigation routing behaviour for the subtree.
 *
 * Pass `routerPrefixes={[]}` to force all navigation to use
 * `window.location.href` — e.g. in the classic environment where React Router
 * cannot handle cross-module navigation.
 *
 * When no `NavigationProvider` is present, the default prefixes apply and
 * `useModuleNavigation` behaves as normal.
 */
export function NavigationProvider({
  children,
  routerPrefixes,
}: {
  children: ReactNode;
  routerPrefixes: readonly string[];
}) {
  const value = useMemo(() => ({ routerPrefixes }), [routerPrefixes]);
  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}
